// Copyright (c) Docugami, Inc. All rights reserved.

import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

const Container = styled.div.attrs({
    'data-componentname': 'Container'
})`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-right: 1px solid;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 1px;
    background-color: rgb(150, 150, 200);
    z-index: 1;
  }
`;

const DragHandle = styled.div`
  width: 100%;
  height: 10px;
  background-color: #f0f0f0;
  position: absolute;
  bottom: 0;
  cursor: row-resize;
  display: flex;
  justify-content: center;
  align-items: center;

  &::before {
    content: '';
    width: 30px;
    height: 4px;
    background-color: #ccc;
    border-radius: 2px;
  }
`;

const cellMinHeight = 100;

interface RowResizeableCellProps {
    onResize: (newHeight: number) => void;
    children?: ReactNode;
}

export const RowResizeableCell: React.FC<RowResizeableCellProps> = ({
    onResize,
    children
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [mouseOver, setMouseOver] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const startYRef = useRef<number>(0);
    const startHeightRef = useRef<number>(0);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        setIsDragging(true);
        startYRef.current = e.clientY;
        startHeightRef.current = containerRef.current?.offsetHeight || 0;
    }, []);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging) return;

        const deltaY = e.clientY - startYRef.current;
        const newHeight = Math.max(cellMinHeight, startHeightRef.current + deltaY);

        onResize(newHeight);
    }, [isDragging, onResize]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('mousemove', handleMouseMove);
        }
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, [isDragging, handleMouseUp, handleMouseMove]);

    return (
        <Container
            ref={containerRef}
            onMouseEnter={() => setMouseOver(true)}
            onMouseLeave={() => setMouseOver(false)}
        >
            { children }
            { mouseOver && <DragHandle onMouseDown={handleMouseDown} /> }
        </Container>
    );
};
